'use client';

import React from 'react';
import { Popover } from '@chakra-ui/react';
import { LuChevronDown, LuCheck } from 'react-icons/lu';

import { Button } from './button';
import { Box } from './box';
import { Stack } from './stack';
import { Text } from './text';
import { Portal } from './portal';
import type { ButtonProps } from './button';
import type { IconButtonProps } from './icon-button';

// Use Menu namespace components directly

export interface DropdownItem {
  id: string;
  label: string;
  value?: unknown;
  icon?: React.ReactNode;
  disabled?: boolean;
  destructive?: boolean;
  onSelect?: () => void;
  items?: DropdownItem[]; // For nested items
  command?: string; // For keyboard shortcuts
}

export interface DropdownProps {
  items: DropdownItem[];
  trigger?: React.ReactNode;
  triggerProps?: ButtonProps | IconButtonProps;
  placeholder?: string;
  value?: string | string[];
  onChange?: (value: string | string[]) => void;
  multiple?: boolean;
  closeOnSelect?: boolean;
  isLazy?: boolean;
  placement?:
    | 'top'
    | 'bottom'
    | 'left'
    | 'right'
    | 'top-start'
    | 'top-end'
    | 'bottom-start'
    | 'bottom-end'
    | 'left-start'
    | 'left-end'
    | 'right-start'
    | 'right-end';
  size?: 'sm' | 'md';
  variant?: 'subtle' | 'solid';
  colorScheme?: string;
  onOpenChange?: (open: boolean) => void;
}

export function Dropdown({
  items,
  trigger,
  triggerProps,
  placeholder = 'Select option',
  value,
  onChange,
  multiple = false,
  closeOnSelect = !multiple,
  isLazy = true,
  placement = 'bottom-start',
  size = 'md',
  variant: _variant = 'subtle',
  colorScheme: _colorScheme,
  onOpenChange,
}: DropdownProps) {
  const [selectedValues, setSelectedValues] = React.useState<string[]>(
    multiple && value ? (Array.isArray(value) ? value : [value]) : []
  );

  const handleSelect = (itemId: string, onSelectCallback?: () => void) => {
    if (!multiple) {
      onChange?.(itemId);
    }
    onSelectCallback?.();
  };

  const handleCheckboxChange = (itemId: string) => {
    const newValues = selectedValues.includes(itemId)
      ? selectedValues.filter((v) => v !== itemId)
      : [...selectedValues, itemId];
    setSelectedValues(newValues);
    onChange?.(newValues);
  };

  const getSelectedLabel = () => {
    if (!value) return placeholder;

    if (multiple && Array.isArray(value)) {
      const labels = value.map((v) => findItemById(items, v)?.label).filter(Boolean);
      return labels.length > 0 ? labels.join(', ') : placeholder;
    }

    return findItemById(items, value as string)?.label || placeholder;
  };

  const renderItems = (items: DropdownItem[]): React.ReactNode => {
    return items.map((item, index) => {
      // Handle separator
      if (item.id === 'separator') {
        return (
          <Box key={`separator-${index}`} height="1px" backgroundColor="#E2E8F0" marginY="4px" />
        );
      }

      // Handle group
      if (item.items && item.items.length > 0) {
        return (
          <Box key={item.id}>
            <Text fontSize="sm" fontWeight="semibold" padding="8px 12px" color="#6B7280">
              {item.label}
            </Text>
            {item.items && renderItems(item.items)}
          </Box>
        );
      }

      // Handle checkbox item (for multiple selection)
      if (multiple) {
        return (
          <Box
            key={item.id}
            padding="8px 12px"
            cursor={item.disabled ? 'not-allowed' : 'pointer'}
            opacity={item.disabled ? 0.6 : 1}
            backgroundColor={selectedValues.includes(item.id) ? '#EBF8FF' : 'transparent'}
            _hover={{ backgroundColor: item.disabled ? 'transparent' : '#F7FAFC' }}
            onClick={() => !item.disabled && handleCheckboxChange(item.id)}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" gap={2} alignItems="center">
              {item.icon}
              <Text>{item.label}</Text>
            </Stack>
            {selectedValues.includes(item.id) && <LuCheck size={16} />}
          </Box>
        );
      }

      // Handle regular item
      return (
        <Box
          key={item.id}
          padding="8px 12px"
          cursor={item.disabled ? 'not-allowed' : 'pointer'}
          opacity={item.disabled ? 0.6 : 1}
          color={item.destructive ? '#EF4444' : 'inherit'}
          _hover={{ backgroundColor: item.disabled ? 'transparent' : '#F7FAFC' }}
          onClick={() => !item.disabled && handleSelect(item.id, item.onSelect)}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" gap={2} alignItems="center">
            {item.icon}
            <Text flex="1">{item.label}</Text>
          </Stack>
          {item.command && (
            <Text fontSize="sm" color="#6B7280">
              {item.command}
            </Text>
          )}
        </Box>
      );
    });
  };

  return (
    <Popover.Root
      lazyMount={isLazy}
      closeOnInteractOutside={closeOnSelect}
      positioning={{ placement }}
      onOpenChange={onOpenChange ? (e: { open: boolean }) => onOpenChange(e.open) : undefined}
    >
      <Popover.Trigger {...({} as Record<string, unknown> & { children?: React.ReactNode })}>
        {trigger || (
          <Button
            variant="outline"
            size={size}
            aria-label={placeholder}
            {...({ 'aria-haspopup': 'menu', 'aria-expanded': undefined } as Record<
              string,
              unknown
            >)}
            {...(triggerProps as Record<string, unknown>)}
          >
            {getSelectedLabel()}
            <LuChevronDown />
          </Button>
        )}
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner {...({} as Record<string, unknown> & { children?: React.ReactNode })}>
          <Popover.Content {...({} as Record<string, unknown> & { children?: React.ReactNode })}>
            <Box
              backgroundColor="white"
              borderRadius="md"
              boxShadow="lg"
              border="1px solid #E2E8F0"
              padding="4px"
              minWidth="200px"
              pointerEvents="auto" // Ensure clicks work inside modal dialogs
            >
              {renderItems(items)}
            </Box>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
}

// Helper function to find item by id
function findItemById(items: DropdownItem[], id: string): DropdownItem | undefined {
  for (const item of items) {
    if (item.id === id) return item;
    if (item.items) {
      const found = findItemById(item.items, id);
      if (found) return found;
    }
  }
  return undefined;
}

// Select Component - A more form-oriented dropdown with radio selection
export interface SelectItem {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps {
  items: SelectItem[];
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  size?: 'sm' | 'md';
  variant?: 'subtle' | 'solid';
  isDisabled?: boolean;
  isInvalid?: boolean;
  isRequired?: boolean;
}

export function DropdownSelect({
  items,
  value,
  onChange,
  placeholder = 'Select option',
  size = 'md',
  variant: _variant2 = 'subtle',
  isDisabled,
  isInvalid,
  isRequired,
}: SelectProps) {
  const [selectedValue, setSelectedValue] = React.useState(value || '');

  const handleSelect = (val: string) => {
    setSelectedValue(val);
    onChange?.(val);
  };

  return (
    <Popover.Root positioning={{ placement: 'bottom-start' }}>
      <Popover.Trigger {...({} as Record<string, unknown> & { children?: React.ReactNode })}>
        <Button
          variant="outline"
          size={size}
          disabled={isDisabled}
          aria-label={placeholder}
          fullWidth
          {...({
            'aria-invalid': isInvalid,
            'aria-required': isRequired,
            'aria-haspopup': 'listbox',
            'aria-expanded': undefined,
          } as Record<string, unknown>)}
        >
          {selectedValue ? items.find((item) => item.value === selectedValue)?.label : placeholder}
          <LuChevronDown />
        </Button>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner {...({} as Record<string, unknown> & { children?: React.ReactNode })}>
          <Popover.Content {...({} as Record<string, unknown> & { children?: React.ReactNode })}>
            <Box
              backgroundColor="white"
              borderRadius="md"
              boxShadow="lg"
              border="1px solid #E2E8F0"
              padding="4px"
              minWidth="200px"
              pointerEvents="auto" // Ensure clicks work inside modal dialogs
            >
              {items.map((item) => (
                <Box
                  key={item.value}
                  padding="8px 12px"
                  cursor={item.disabled ? 'not-allowed' : 'pointer'}
                  opacity={item.disabled ? 0.6 : 1}
                  backgroundColor={selectedValue === item.value ? '#EBF8FF' : 'transparent'}
                  _hover={{ backgroundColor: item.disabled ? 'transparent' : '#F7FAFC' }}
                  onClick={() => !item.disabled && handleSelect(item.value)}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Text>{item.label}</Text>
                  {selectedValue === item.value && <LuCheck size={16} />}
                </Box>
              ))}
            </Box>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
}

// MultiSelect Component - Checkbox-based multiple selection
export interface MultiSelectProps {
  items: SelectItem[];
  value?: string[];
  onChange?: (value: string[]) => void;
  placeholder?: string;
  size?: 'sm' | 'md';
  variant?: 'subtle' | 'solid';
  isDisabled?: boolean;
}

export function MultiSelect({
  items,
  value = [],
  onChange,
  placeholder = 'Select options',
  size = 'md',
  variant: _variant2 = 'subtle',
  isDisabled,
}: MultiSelectProps) {
  const handleToggle = (itemValue: string) => {
    const newValues = value.includes(itemValue)
      ? value.filter((v) => v !== itemValue)
      : [...value, itemValue];
    onChange?.(newValues);
  };

  const getLabel = () => {
    if (value.length === 0) return placeholder;
    if (value.length === 1) {
      return items.find((item) => item.value === value[0])?.label || placeholder;
    }
    return `${value.length} selected`;
  };

  return (
    <Popover.Root closeOnInteractOutside={false} positioning={{ placement: 'bottom-start' }}>
      <Popover.Trigger {...({} as Record<string, unknown> & { children?: React.ReactNode })}>
        <Button
          variant="outline"
          size={size}
          disabled={isDisabled}
          aria-label={placeholder}
          fullWidth
          {...({
            'aria-haspopup': 'menu',
            'aria-expanded': undefined,
            'aria-describedby': value.length > 0 ? `${value.length} items selected` : undefined,
          } as Record<string, unknown>)}
        >
          {getLabel()}
          <LuChevronDown />
        </Button>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner {...({} as Record<string, unknown> & { children?: React.ReactNode })}>
          <Popover.Content {...({} as Record<string, unknown> & { children?: React.ReactNode })}>
            <Box
              backgroundColor="white"
              borderRadius="md"
              boxShadow="lg"
              border="1px solid #E2E8F0"
              padding="4px"
              minWidth="200px"
              pointerEvents="auto" // Ensure clicks work inside modal dialogs
            >
              {items.map((item) => (
                <Box
                  key={item.value}
                  padding="8px 12px"
                  cursor={item.disabled ? 'not-allowed' : 'pointer'}
                  opacity={item.disabled ? 0.6 : 1}
                  backgroundColor={value.includes(item.value) ? '#EBF8FF' : 'transparent'}
                  _hover={{ backgroundColor: item.disabled ? 'transparent' : '#F7FAFC' }}
                  onClick={() => !item.disabled && handleToggle(item.value)}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Text>{item.label}</Text>
                  {value.includes(item.value) && <LuCheck size={16} />}
                </Box>
              ))}
            </Box>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
}

// Action Menu - For action dropdowns
export interface ActionMenuItem {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  destructive?: boolean;
  command?: string;
}

export interface ActionMenuProps {
  items: ActionMenuItem[];
  trigger?: React.ReactNode;
  label?: string;
  size?: 'sm' | 'md';
  placement?:
    | 'top'
    | 'bottom'
    | 'left'
    | 'right'
    | 'top-start'
    | 'top-end'
    | 'bottom-start'
    | 'bottom-end'
    | 'left-start'
    | 'left-end'
    | 'right-start'
    | 'right-end';
}

export function ActionMenu({
  items,
  trigger,
  label = 'Actions',
  size = 'md',
  placement = 'bottom-end',
}: ActionMenuProps) {
  const dropdownItems: DropdownItem[] = items.map((item, index) => ({
    id: `action-${index}`,
    label: item.label,
    icon: item.icon,
    disabled: item.disabled,
    destructive: item.destructive,
    onSelect: item.onClick,
    command: item.command,
  }));

  return (
    <Dropdown
      items={dropdownItems}
      trigger={trigger}
      placeholder={label}
      size={size}
      placement={placement}
      closeOnSelect={true}
    />
  );
}

// Context Menu - Right-click menu
export interface ContextMenuProps {
  items: ActionMenuItem[];
  children: React.ReactNode;
}

export function ContextMenu({ items, children }: ContextMenuProps) {
  return (
    <Popover.Root>
      <Popover.Trigger {...({} as Record<string, unknown> & { children?: React.ReactNode })}>
        {children}
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner {...({} as Record<string, unknown> & { children?: React.ReactNode })}>
          <Popover.Content {...({} as Record<string, unknown> & { children?: React.ReactNode })}>
            <Box
              backgroundColor="white"
              borderRadius="md"
              boxShadow="lg"
              border="1px solid #E2E8F0"
              padding="4px"
              minWidth="200px"
              pointerEvents="auto" // Ensure clicks work inside modal dialogs
            >
              {items.map((item, index) => (
                <Box
                  key={`context-${index}`}
                  padding="8px 12px"
                  cursor={item.disabled ? 'not-allowed' : 'pointer'}
                  opacity={item.disabled ? 0.6 : 1}
                  color={item.destructive ? '#EF4444' : 'inherit'}
                  _hover={{ backgroundColor: item.disabled ? 'transparent' : '#F7FAFC' }}
                  onClick={() => !item.disabled && item.onClick?.()}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack direction="row" gap={2} alignItems="center">
                    {item.icon}
                    <Text flex="1">{item.label}</Text>
                  </Stack>
                  {item.command && (
                    <Text fontSize="sm" color="#6B7280">
                      {item.command}
                    </Text>
                  )}
                </Box>
              ))}
            </Box>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
}
